import { ThemedText } from '@/components/themed-text';
import { Input } from '@/components/ui/input';
import { formatBRL } from '@/content/facts';
import {
  billableHoursPerMonth,
  computeHourlyRate,
  HOURLY_RATE_EXAMPLE,
  type HourlyRateInput,
  parseDecimal,
  parseMoney,
} from '@/lib/hourly-rate';
import React, { useState } from 'react';
import { View } from 'react-native';

type FieldKey = keyof HourlyRateInput;

interface Field {
  key: FieldKey;
  label: string;
  unit: string;
  /** R$ aceita ponto de milhar; % e horas aceitam ponto decimal. */
  money?: boolean;
  note?: string;
}

const FIELDS: Field[] = [
  { key: 'income', label: 'Quero ganhar por mês', unit: 'R$', money: true },
  { key: 'costs', label: 'Custos fixos por mês', unit: 'R$', money: true, note: 'No MEI, o imposto fixo do mês entra aqui.' },
  { key: 'taxPercent', label: 'Imposto', unit: '%', note: '0% no MEI. 6% é a 1ª faixa do Simples Nacional, anexo III.' },
  { key: 'hoursPerWeek', label: 'Horas por semana', unit: 'h' },
  { key: 'weeksOff', label: 'Semanas paradas por ano', unit: 'semanas', note: 'Férias, feriados e dias doente.' },
  { key: 'billablePercent', label: 'Horas que viram atendimento pago', unit: '% do tempo' },
];

const toText = (input: HourlyRateInput) =>
  Object.fromEntries(Object.entries(input).map(([k, v]) => [k, String(v).replace('.', ',')])) as Record<FieldKey, string>;

const formatHours = (h: number) => h.toLocaleString('pt-BR', { maximumFractionDigits: 1 });

/**
 * Calculadora "quanto cobrar por hora" do guia. Abre com o exemplo do texto
 * já preenchido, para o resultado sair no HTML pré-renderizado: quem chega da
 * busca vê a conta pronta antes de mexer.
 */
export const HourlyRateCalculator: React.FC = () => {
  const [values, setValues] = useState<Record<FieldKey, string>>(() => toText(HOURLY_RATE_EXAMPLE));

  const input = Object.fromEntries(
    FIELDS.map((f) => [f.key, f.money ? parseMoney(values[f.key]) : parseDecimal(values[f.key])]),
  ) as unknown as HourlyRateInput;
  const rate = computeHourlyRate(input);
  const hours = billableHoursPerMonth(input);

  return (
    <View className="rounded-2xl border border-neutral-900/5 bg-card p-4 md:p-8">
      <ThemedText role="heading" aria-level={2} className="font-bold text-xl">
        Calculadora do valor da hora
      </ThemedText>
      <View className="mt-4 gap-4 md:flex-row md:flex-wrap">
        {FIELDS.map((field) => (
          <View key={field.key} className="md:w-[48%]">
            <View className="flex-row items-baseline justify-between gap-2">
              <ThemedText className="flex-1 font-medium text-sm text-neutral-700">{field.label}</ThemedText>
              <ThemedText className="text-xs text-neutral-500">{field.unit}</ThemedText>
            </View>
            <Input
              className="mt-1"
              keyboardType="decimal-pad"
              inputMode="decimal"
              value={values[field.key]}
              accessibilityLabel={`${field.label} (${field.unit})`}
              onChangeText={(text) => setValues((prev) => ({ ...prev, [field.key]: text }))}
            />
            {field.note ? <ThemedText className="mt-1 text-xs text-neutral-500">{field.note}</ThemedText> : null}
          </View>
        ))}
      </View>
      <View className="mt-6 rounded-xl bg-primary-50 p-4" aria-live="polite">
        {rate && hours ? (
          <>
            <ThemedText className="text-sm text-neutral-600">Sua hora deveria custar pelo menos</ThemedText>
            <ThemedText className="mt-1 font-bold text-3xl text-primary">{formatBRL(rate)}</ThemedText>
            <ThemedText className="mt-1 text-sm text-neutral-600">
              {formatHours(hours)} horas vendáveis por mês. Um atendimento de 50 minutos: {formatBRL((rate * 50) / 60)}
            </ThemedText>
          </>
        ) : (
          <ThemedText className="text-sm text-neutral-600">Preencha os campos com números para ver o valor.</ThemedText>
        )}
      </View>
    </View>
  );
};
